import { Link } from "@tanstack/react-router";
import {
  Announcement03,
  CheckVerified01,
  LineChartUp02,
  Link03,
  Signal01,
  UserX01,
  VideoRecorder,
} from "@untitledui/icons";
import { MoreVertical, Plus, VideoOff } from "lucide-react";
import { useMemo } from "react";
import { useProductDailyMetrics } from "@/api/product";
import { type ProductAdCampaignsResponse, useProductAdCampaigns } from "@/api/products";
import { useTikTokAdsAuthUrl, useTikTokAdsConnectionStatus } from "@/api/tiktok-ads";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import type { ChartConfig, TimeSeriesDataPoint } from "@/components/MetricsBarChartView";
import { MetricsLineChartView } from "@/components/MetricsLineChartView";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMerchant } from "@/providers/MerchantProvider";
import { videoExplorerRoute } from "@/router";
import { getCreatorName } from "@/utils/creator-name";

type PerformanceTabProps = {
  productName: string;
  related:
    | {
        creators: Array<{
          creatorId: string;
          creatorName: string;
          nickname: string | null;
          thumbnail: string | null;
          gmv: number;
          videos: number;
        }>;
        videos: Array<{
          videoId: string;
          creatorId: string | null;
          urlFirstFrame: string | null;
          videoUrl: string | null;
          videoTitle: string;
          gmv: number;
          unitSales: number;
          views: number;
          gpm: number;
        }>;
      }
    | null
    | undefined;
  currencyFormatter: Intl.NumberFormat;
  numFormatter: Intl.NumberFormat;
  productId: string;
};

interface GMVDataPoint extends TimeSeriesDataPoint {
  revenue: number;
}

export const PerformanceTab = ({
  related,
  currencyFormatter,
  numFormatter,
  productId,
  productName,
}: PerformanceTabProps) => {
  const { data: dailyMetrics, isLoading: isLoadingDailyMetrics } = useProductDailyMetrics(productId);
  const { data: adCampaigns, isLoading: isLoadingAdCampaigns } = useProductAdCampaigns(productId);
  const { data: adsConnectionStatus } = useTikTokAdsConnectionStatus();
  const { data: adsAuthData } = useTikTokAdsAuthUrl();
  const merchant = useMerchant();

  const isAdsConnected = adsConnectionStatus?.connected && !adsConnectionStatus?.needsReauth;

  const sortedCampaigns = useMemo(() => adCampaigns?.campaigns ?? [], [adCampaigns?.campaigns]);

  // Transform data for the chart
  const chartData = useMemo<GMVDataPoint[]>(() => {
    if (!dailyMetrics || dailyMetrics.length === 0) {
      // Generate 30 days of zero data when no metrics available
      const data: GMVDataPoint[] = [];
      const today = new Date();
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        data.push({
          date: date.toISOString().split("T")[0],
          dateLabel: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          revenue: 0,
        });
      }
      return data;
    }

    return dailyMetrics.map((item) => ({
      date: item.date,
      dateLabel: new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      revenue: item.revenue || 0,
    }));
  }, [dailyMetrics]);

  // Calculate the 5 tick labels to show (offset from edges so line has visual padding)
  const xAxisTicks = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    const len = chartData.length;
    // Offset from edges by ~2-3 days so the line extends beyond the first/last labels
    const edgeOffset = Math.min(2, Math.floor(len * 0.07));
    const usableRange = len - 1 - edgeOffset * 2;
    const indices = [
      edgeOffset,
      edgeOffset + Math.round(usableRange * 0.25),
      edgeOffset + Math.round(usableRange * 0.5),
      edgeOffset + Math.round(usableRange * 0.75),
      len - 1 - edgeOffset,
    ];
    return indices.map((i) => chartData[i].dateLabel);
  }, [chartData]);

  // Chart configuration
  const chartConfig: ChartConfig<GMVDataPoint> = useMemo(
    () => ({
      metrics: ["revenue"],
      formatters: {
        revenue: (value: number) => {
          // Format without currency symbol, showing full number with commas
          return new Intl.NumberFormat("en-US", {
            maximumFractionDigits: 0,
          }).format(value);
        },
      },
      colors: {
        revenue: "#7F56D9",
      },
      labels: {
        revenue: "GMV",
      },
    }),
    [],
  );

  return (
    <div className="space-y-8 mt-8">
      {/* Creators driving top GMV */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <CheckVerified01 className="w-5 h-5 text-tertiary" />
          <h3 className="text-sm font-medium text-secondary">Creators driving top GMV in the last 30 days</h3>
        </div>
        {!related && (
          <div className="flex w-full h-24 justify-center items-center">
            <LoadingIndicator />
          </div>
        )}
        {related?.creators.length === 0 && (
          <div className="w-full bg-gray-50 rounded-lg px-6 py-12 flex flex-col items-center justify-center text-center">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: "#F5F5F5" }}
            >
              <UserX01 className="w-6 h-6" style={{ color: "#717680" }} />
            </div>
            <h4 className="text-sm font-medium text-primary mb-1">No creators generating GMV yet</h4>
            <p className="text-sm text-tertiary max-w-md">
              Top performers will appear here, once creators start <br /> posting videos
            </p>
          </div>
        )}
        {related && related.creators.length > 0 && (
          <div className="flex gap-3 overflow-x-auto">
            {related.creators.map((creator) => {
              const { nickname, handle } = getCreatorName(creator.nickname, creator.creatorName);
              const displayName = nickname ?? handle;
              return (
                <div
                  key={creator.creatorId}
                  className="flex-shrink-0 w-[180px] bg-primary rounded-lg border border-border-primary flex flex-col overflow-hidden"
                >
                  <div className="flex-1 flex flex-col overflow-hidden">
                    {creator.thumbnail && (
                      <img
                        src={creator.thumbnail}
                        alt={displayName ?? "Creator"}
                        className="w-full h-[190px] object-cover"
                      />
                    )}
                    <div className="px-4 py-6 flex flex-col gap-1">
                      <a
                        href={`/creator-explorer?creator_id=${creator.creatorId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-primary line-clamp-1 hover:text-brand-primary transition-colors cursor-pointer"
                      >
                        {displayName}
                      </a>
                      {nickname && handle && (
                        <a
                          href={`/creator-explorer?creator_id=${creator.creatorId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-quaternary line-clamp-1 hover:text-brand-primary transition-colors cursor-pointer"
                        >
                          {handle}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="px-4 py-5 border-t border-border-primary flex gap-10">
                    <div className="flex flex-col">
                      <span className="text-xs text-quaternary">GMV</span>
                      <span className="text-sm font-medium text-tertiary">{currencyFormatter.format(creator.gmv)}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-quaternary">Videos</span>
                      <span className="text-sm font-medium text-tertiary">{numFormatter.format(creator.videos)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* GMV Breakdown Chart */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <LineChartUp02 className="w-5 h-5 text-tertiary" />
          <h3 className="text-sm font-medium text-secondary">GMV breakdown over the last 30 days</h3>
        </div>
        {isLoadingDailyMetrics ? (
          <div className="flex w-full h-[300px] justify-center items-center">
            <LoadingIndicator />
          </div>
        ) : (
          <div className="h-[300px]">
            <MetricsLineChartView<GMVDataPoint>
              data={chartData}
              config={chartConfig}
              chartType="line"
              showLegend={false}
              rotateXAxisLabels={false}
              xAxisTicks={xAxisTicks}
              height="100%"
              yAxisConfig={{ left: { width: 50 } }}
              margin={{ top: 20, right: 0, left: 0, bottom: 20 }}
            />
          </div>
        )}
      </div>

      {/* Ad's Summary */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Announcement03 className="w-5 h-5 text-tertiary" />
          <h3 className="text-sm font-medium text-secondary">Ad's summary</h3>
        </div>
        {isLoadingAdCampaigns ? (
          <div className="flex w-full h-24 justify-center items-center">
            <LoadingIndicator />
          </div>
        ) : !isAdsConnected ? (
          <div className="relative rounded-lg overflow-hidden bg-[linear-gradient(180deg,rgba(79,69,109,0.90)_48.16%,rgba(32,24,54,0.90)_90%)] px-8 py-12 flex items-center justify-between">
            <div className="flex flex-col gap-2">
              <h4 className="text-white text-base font-medium">Know what you're spending to promote this product</h4>
              <p className="text-white/80 text-sm">Pull in ad spend, ROAS, and conversion costs automatically.</p>
            </div>
            <a
              href={adsAuthData?.authUrl ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white px-4 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors flex-shrink-0"
            >
              <Link03 className="w-4 h-4" />
              Connect TikTok Ads
            </a>
          </div>
        ) : adCampaigns && adCampaigns.summary.totalCampaigns > 0 ? (
          <div className="border border-border-primary rounded-lg divide-x divide-border-primary flex">
            <div className="flex-1 text-center px-4 py-5">
              <div className="text-lg font-semibold text-primary">{adCampaigns.summary.totalCampaigns}</div>
              <div className="text-sm text-tertiary">Active Campaigns</div>
            </div>
            <div className="flex-1 text-center px-4 py-5">
              <div className="text-lg font-semibold text-primary">
                {"totalCreators" in adCampaigns.summary ? adCampaigns.summary.totalCreators : 0}
              </div>
              <div className="text-sm text-tertiary">Creators in Campaigns</div>
            </div>
          </div>
        ) : (
          <div className="w-full bg-gray-50 rounded-lg px-6 py-12 flex flex-col items-center justify-center text-center">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: "#F5F5F5" }}
            >
              <VideoOff className="w-6 h-6" style={{ color: "#717680" }} />
            </div>
            <h4 className="text-sm font-medium text-primary mb-1">No active ad campaigns yet</h4>
            <p className="text-sm text-tertiary max-w-md">
              Campaign metrics will appear here once you start <br /> running TikTok Ads for this product
            </p>
          </div>
        )}
      </div>

      {/* Active Campaigns Table */}
      {adCampaigns && isAdsConnected && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Signal01 className="w-5 h-5 text-tertiary" />
              <h3 className="text-sm font-medium text-secondary">Active campaigns</h3>
            </div>
            {adCampaigns.campaigns.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="p-1.5 hover:bg-gray-100 rounded flex-shrink-0 transition-colors"
                    aria-label="Campaigns actions"
                  >
                    <MoreVertical className="w-4 h-4 text-gray-600" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={5}>
                  <DropdownMenuItem asChild>
                    <Link
                      to={videoExplorerRoute.to}
                      params={{ merchantId: merchant }}
                      search={{
                        product: [{ id: productId, label: productName }],
                        campaign: adCampaigns.campaigns.map((c) => ({ id: c.campaignId, label: c.campaignName })),
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cursor-pointer flex items-center gap-2"
                    >
                      <VideoRecorder className="w-4 h-4 text-tertiary" />
                      See videos in all campaigns
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {adCampaigns.campaigns.length > 0 ? (
            <div className="border border-border-primary rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-border-primary">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Campaign Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-tertiary w-10" />
                  </tr>
                </thead>
                <tbody>
                  {sortedCampaigns.map((campaign) => (
                    <CampaignRow
                      key={campaign.campaignId}
                      campaign={campaign}
                      productId={productId}
                      productName={productName}
                      merchantId={merchant}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="w-full bg-gray-50 rounded-lg px-6 py-12 flex flex-col items-center justify-center text-center">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: "#F5F5F5" }}
              >
                <Signal01 className="w-6 h-6" style={{ color: "#717680" }} />
              </div>
              <h4 className="text-sm font-medium text-primary mb-1">0 Active campaigns</h4>
              <a
                href="https://ads.tiktok.com/i18n/campaign"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create an Ad's Campaign
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

type Campaign = ProductAdCampaignsResponse["campaigns"][number];

type CampaignRowProps = {
  campaign: Campaign;
  productId: string;
  productName: string;
  merchantId: string;
};

export const CampaignRow = ({ campaign, productId, productName, merchantId }: CampaignRowProps) => {
  const tiktokCampaignUrl = `https://ads.tiktok.com/i18n/perf/campaign?aadvid=${campaign.advertiserId}&keyword=${campaign.campaignId}`;

  return (
    <tr className="border-b border-border-primary last:border-0 hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <a
            href={tiktokCampaignUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary font-medium hover:text-brand-primary transition-colors underline decoration-dotted"
          >
            {campaign.campaignName}
          </a>
        </div>
      </td>
      <td className="px-4 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1.5 hover:bg-gray-100 rounded flex-shrink-0 transition-colors"
              aria-label="Campaign actions"
            >
              <MoreVertical className="w-4 h-4 text-gray-600" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={5}>
            <DropdownMenuItem asChild>
              <Link
                to={videoExplorerRoute.to}
                params={{ merchantId }}
                search={{
                  product: [{ id: productId, label: productName }],
                  campaign: [{ id: campaign.campaignId, label: campaign.campaignName }],
                }}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer flex items-center gap-2"
              >
                <VideoRecorder className="w-4 h-4 text-tertiary" />
                See all Videos in Campaign
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
};
